import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Inbox, Plus } from 'lucide-react';
import { Button } from '@qargo-ims/ui';
import { apiFetch } from '@/lib/api/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge, statusVariant } from '@/components/ui/badge';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { formatShortDate } from '@/lib/format';

// Performance review now lives on the employee profile (Reviews tab), not here.
export const TICKET_TYPES = [
  ['termination_request', 'Termination request'],
  ['pip_request', 'PIP request'],
  ['investigation_request', 'Investigation request'],
  ['timecard_issue', 'Timecard issue'],
  ['payroll_issue', 'Payroll issue'],
  ['other', 'Other'],
] as const;

type TicketType = (typeof TICKET_TYPES)[number][0];

interface Ticket {
  id: string;
  type: string;
  typeLabel: string;
  subject: string;
  body: string;
  status: 'open' | 'resolved';
  requester: { entraOid: string; displayName: string | null; email: string | null };
  subjectBarista: { id: string; username: string; displayName: string | null } | null;
  createdAt: string;
  resolvedAt: string | null;
}
interface ListResponse {
  scope: 'all' | 'mine';
  tickets: Ticket[];
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function Hr() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [scope, setScope] = useState<'all' | 'mine'>('mine');
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'resolved'>('open');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    const q = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
    apiFetch<ListResponse>(`/hr-tickets${q}`)
      .then((r) => {
        setTickets(r.tickets);
        setScope(r.scope);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="HR requests"
        description={
          scope === 'all'
            ? 'All HR tickets across the org — submit, track, and resolve.'
            : 'HR tickets you have submitted. HR is notified by email on each new request.'
        }
        actions={
          <Button onClick={() => setSubmitOpen(true)}>
            <Plus className="size-4" aria-hidden /> New request
          </Button>
        }
      />

      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all' | 'open' | 'resolved')}
          className="rounded-md border border-border px-2 py-1 text-sm"
        >
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="all">All</option>
        </select>
        {scope === 'all' ? <Badge variant="muted">org-wide view</Badge> : null}
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : tickets.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No HR requests"
          description="Submit a request and HR will be notified by email."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {tickets.map((t) => (
                <li key={t.id}>
                  <Link
                    to={`/admin/hr/${t.id}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50"
                  >
                    <Badge variant={statusVariant(t.status)}>{t.status}</Badge>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{t.subject}</span>
                      <span className="text-xs text-muted-foreground">
                        {t.typeLabel}
                        {t.subjectBarista ? ` · re: @${t.subjectBarista.username}` : ''}
                        {scope === 'all' && t.requester.displayName
                          ? ` · by ${t.requester.displayName}`
                          : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatShortDate(t.createdAt)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {submitOpen ? (
        <SubmitTicketDialog
          onClose={() => setSubmitOpen(false)}
          onCreated={() => {
            setSubmitOpen(false);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

interface BaristaOption {
  id: string;
  username: string;
  displayName: string | null;
}

function SubmitTicketDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [type, setType] = useState<TicketType>('termination_request');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [subjectBaristaId, setSubjectBaristaId] = useState('');
  const [baristas, setBaristas] = useState<BaristaOption[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Optional barista picker. Only Admins/CLevel/HR/CentralOps can read /users;
  // store managers get a 403 — degrade gracefully by hiding the picker.
  useEffect(() => {
    let active = true;
    apiFetch<{ users: BaristaOption[] }>('/users?tier=store_ops&status=all')
      .then((r) => {
        if (active) setBaristas(r.users);
      })
      .catch(() => {
        if (active) setBaristas([]);
      });
    return () => {
      active = false;
    };
  }, []);

  async function submit() {
    if (!subject.trim() || !body.trim()) {
      setErr('Subject and details are required.');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await apiFetch('/hr-tickets', {
        method: 'POST',
        body: JSON.stringify({
          type,
          subject: subject.trim(),
          body: body.trim(),
          subjectBaristaId: subjectBaristaId || null,
        }),
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'submit failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-6">
      <div className="my-8 w-full max-w-lg space-y-3 rounded-xl bg-card p-6">
        <h3 className="text-lg font-semibold">New HR request</h3>
        <p className="text-xs text-muted-foreground">
          Submitting notifies HR by email. You’ll be emailed back when it’s resolved.
        </p>
        <label className="block text-sm">
          Request type
          <select
            value={type}
            onChange={(e) => setType(e.target.value as TicketType)}
            className={`mt-1 ${inputClass}`}
          >
            {TICKET_TYPES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          Subject
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={256}
            className={`mt-1 ${inputClass}`}
            placeholder="One-line summary"
            autoFocus
          />
        </label>
        {baristas && baristas.length > 0 ? (
          <div className="text-sm">
            <span className="block">About (optional)</span>
            <EmployeePicker
              baristas={baristas}
              value={subjectBaristaId}
              onChange={setSubjectBaristaId}
            />
          </div>
        ) : null}
        <label className="block text-sm">
          Details
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            maxLength={8000}
            className={`mt-1 ${inputClass}`}
            placeholder="What do you need from HR? Include relevant dates and context."
          />
        </label>
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
            Cancel
          </button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit request'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function baristaLabel(b: BaristaOption): string {
  return b.displayName ? `${b.displayName} (@${b.username})` : `@${b.username}`;
}

/** Searchable employee picker — filters by name/username so the list stays
 * usable as the roster grows. */
function EmployeePicker({
  baristas,
  value,
  onChange,
}: {
  baristas: BaristaOption[];
  value: string;
  onChange: (id: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const selected = baristas.find((b) => b.id === value) ?? null;

  if (selected) {
    return (
      <div className="mt-1 flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm">
        <span>{baristaLabel(selected)}</span>
        <button
          type="button"
          onClick={() => {
            onChange('');
            setQuery('');
          }}
          className="text-xs text-muted-foreground hover:text-destructive"
        >
          Clear
        </button>
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const matches = (
    q
      ? baristas.filter(
          (b) =>
            (b.displayName ?? '').toLowerCase().includes(q) || b.username.toLowerCase().includes(q),
        )
      : baristas
  ).slice(0, 8);

  return (
    <div className="relative mt-1">
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search employee by name…"
        className={inputClass}
      />
      {open && matches.length > 0 ? (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-border bg-card shadow-md">
          {matches.map((b) => (
            <li key={b.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(b.id);
                  setQuery('');
                  setOpen(false);
                }}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
              >
                {baristaLabel(b)}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
